<script setup lang="ts">
// In-app confirmation modal. Designed to replace native window.confirm()
// across the app via the useConfirm() composable in `confirm.ts`.

import { computed, watch, ref, onMounted, onBeforeUnmount } from 'vue'
import { AlertTriangle, X } from 'lucide-vue-next'
import { confirmState, resolveConfirm } from '@/composables/useConfirm'

const cancelRef = ref<HTMLButtonElement | null>(null)

watch(() => confirmState.value.open, (open) => {
  if (open) requestAnimationFrame(() => cancelRef.value?.focus())
})

function onKeyDown(e: KeyboardEvent) {
  if (!confirmState.value.open) return
  if (e.key === 'Escape') { e.preventDefault(); resolveConfirm(false) }
  else if (e.key === 'Enter') { e.preventDefault(); resolveConfirm(true) }
}

onMounted(() => window.addEventListener('keydown', onKeyDown))
onBeforeUnmount(() => window.removeEventListener('keydown', onKeyDown))

const variantClass = computed(() => confirmState.value.danger ? 'text-danger-text' : 'text-primary')
</script>

<template>
  <Teleport to="body">
    <div
      v-if="confirmState.open"
      class="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style="background: rgba(0,0,0,0.45);"
      @click.self="resolveConfirm(false)"
    >
      <div
        class="rounded-lg border border-surface-border shadow-2xl w-full max-w-sm p-4"
        style="background: var(--surface-opaque);"
        @click.stop
      >
        <div class="flex items-start gap-2 mb-3">
          <AlertTriangle v-if="confirmState.danger" :size="18" class="mt-0.5 shrink-0 text-danger-text" />
          <div class="flex-1 min-w-0">
            <h3 class="text-sm font-semibold text-ink-primary" :class="variantClass">{{ confirmState.title }}</h3>
            <p v-if="confirmState.message" class="text-xs text-ink-secondary mt-1 whitespace-pre-wrap">{{ confirmState.message }}</p>
          </div>
          <button
            class="btn btn-ghost btn-icon text-ink-tertiary -mt-1 -mr-1"
            title="Cancel"
            @click="resolveConfirm(false)"
          >
            <X :size="14" />
          </button>
        </div>
        <div class="flex items-center justify-end gap-2">
          <button
            ref="cancelRef"
            class="btn btn-ghost text-xs"
            @click="resolveConfirm(false)"
          >{{ confirmState.cancelLabel || 'Cancel' }}</button>
          <button
            class="btn text-xs"
            :class="confirmState.danger ? '' : 'btn-primary'"
            :style="confirmState.danger ? 'background: var(--danger-base); color: white;' : ''"
            @click="resolveConfirm(true)"
          >{{ confirmState.confirmLabel || 'Confirm' }}</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
